import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/team", label: "Team" },
  { to: "/investors", label: "Investors" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 border-b border-mm-line ${
        scrolled ? "shadow-[0_4px_20px_rgba(11,31,51,0.06)]" : ""
      }`}
    >
      <div className="h-1 w-full flex" aria-hidden="true">
        <span className="flex-1 bg-brand-orange" />
        <span className="flex-1 bg-brand-teal" />
        <span className="flex-1 bg-brand-red" />
        <span className="flex-1 bg-brand-green" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/mars-mining-logo.png" alt="Mars Mining Limited" className="h-9 w-auto transition-transform duration-300 group-hover:scale-105" />
          <span className="flex flex-col leading-none">
            <span className="font-display font-bold text-lg tracking-tight text-mm-ink">MARS MINING</span>
            <span className="font-mono text-[0.58rem] tracking-[0.3em] uppercase text-mm-muted mt-0.5">Limited · London</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[0.95rem] font-medium transition-colors ${
                  isActive ? "text-mm-blue" : "text-mm-ink/70 hover:text-mm-blue"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-mm-blue text-white text-sm font-semibold rounded-md hover:bg-mm-navy transition-colors"
        >
          Enquire <ArrowRight size={15} />
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-mm-ink p-2"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-white border-t border-mm-line"
          >
            <div className="px-6 py-5 flex flex-col">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 border-b border-mm-line font-display font-semibold text-lg ${
                      isActive ? "text-mm-blue" : "text-mm-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-nav-cta" className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 bg-mm-blue text-white font-semibold rounded-md">
                Enquire <ArrowRight size={16} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
